import { inject, Injectable } from "@angular/core";
import { HeaderResults } from "./header-model";
import { mockApiHeaderMenuJsonResponse } from "../common/utils/api-mocks";
import { BaseService } from "../common/services/base-service";
import { from, Observable, ReplaySubject, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { ReusableService } from "../common/services/reusable-services";
import { CacheService } from "../common/services/cache-service";
import { API_PATHS } from "../common/constants/api-paths";
import { ApiResponse } from "../common/models/api-response.model";
import { Language } from "../common/models/language";
import { CACHE_KEYS } from "../common/constants/cache-keys";

@Injectable({
  providedIn: "root",
})
export class HeaderService {
  baseService = inject(BaseService);
  reusableService = inject(ReusableService);
  cacheService = inject(CacheService);

  private readonly headerResponse$ = new ReplaySubject<ApiResponse<HeaderResults>>(1);

  getHeader(): Observable<ApiResponse<HeaderResults>> {
    // When STUBS is true use the local mock response, otherwise call backend
    if (!environment.STUBS) {
      return mockApiHeaderMenuJsonResponse().pipe(
        tap(response => {
          this.setHeaderResponse(response);
        })
      );
    } else {
      // getApplicableLanguage() may return undefined if supported languages are not
      // yet cached (e.g. header initialises before page resolver). Fall back to
      // English so the request doesn't throw and wipe out all header labels.
      const language: Language = this.reusableService.getApplicableLanguage()
        ?? ({ languageCode: 'en', countryCode: 'US' } as Language);

      //get userDetails from cache
      const userDetails = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_DETAILS)
        ?? { region: 'global', market: 'global', userName: '', roleName: '', isAdmin: false };

      const params = {
        componentType: "",
        experienceFragments: [`/content/srp/${userDetails?.region?.toLowerCase()}/${userDetails.market?.toLowerCase()}/${language.languageCode}/header`],
        userDetails: {
          userName: userDetails?.userName,
          userId: "",
          email: "",
          userType: "",
          market: "",
          isAdmin: userDetails?.isAdmin,
          currentContext: {
            businessUnit: "",
            roleName: userDetails?.roleName,
            dealerCode: "",
            dealerName: "",
            languageCode: "",
          },
          userFirstName: "",
          userLastName: "",
          sessionId: "",
        },
      };
      return from(this.baseService.post<ApiResponse<HeaderResults>>(API_PATHS.PORTAL.GET_HEADER, params)).pipe(
        tap(response => {
          this.setHeaderResponse(response);
        }));
    }
  }


  private setHeaderResponse(response: ApiResponse<HeaderResults>) {
    this.headerResponse$.next(response);
  }

  getHeaderResponseFromObservable(): Observable<ApiResponse<HeaderResults>> {
    return this.headerResponse$.asObservable();
  }
}